import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Table } from 'antd'
import PerText from '../../../components/custom_components/Text'
import { PerTableActionButtons } from '../../../components/custom_components/TableActionButtons'
import { KudosDto, KudosFilterInput } from '../../../types'
import { useAuth } from '../../../context/auth-context'
import { PerAvatarPlus } from '../../../components/custom_components/AvatarPlus'
import { useDateTime } from '../../../shared/useDateTime'
import { NameDisplayPreference } from '../../../graphql/enum/name-display-preference.enum'
import { ApolloError } from '@apollo/client'
import classNames from 'classnames'
import { PerEmpty } from '../../../components/custom_components/Empty'
import DeleteAttentionPointModal from '../../../components/custom_components/DeleteAttentionPointModal/DeleteAttentionPointModal'
import { AttentionPointDetailsModal } from '../../../components/custom_components/AttentionPointDetailsModal/AttentionPointDetailsModal'
import { NewLoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors/NewLoadingAndErrors'
import BackgroundBox from '../../../components/custom_components/BackgroundBox'

interface AttentionPointTableProps {
  attentionPoints?: KudosDto[]
  loading: boolean
  isLoadingMore: boolean
  error?: ApolloError
  filters: KudosFilterInput
  showMoreButtonVisible: boolean
}

export const AttentionPointTable: FC<AttentionPointTableProps> = ({
  attentionPoints,
  loading,
  isLoadingMore,
  error,
  filters,
  showMoreButtonVisible,
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { formatDateShort } = useDateTime(user)
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [attentionPointToDelete, setAttentionPointToDelete] = useState<KudosDto | null>(null)
  const [viewModalVisible, setViewModalVisible] = useState(false)
  const [attentionPointToView, setAttentionPointToView] = useState<KudosDto | null>(null)

  if ((loading && !isLoadingMore) || error || !attentionPoints) {
    return (
      <BackgroundBox r height="withKudosInfoBox">
        <NewLoadingAndErrors isLoading={loading && !isLoadingMore} error={error} hasNoData={!attentionPoints} showSpinner t={t} />
      </BackgroundBox>
    )
  }

  const handleDeleteClick = (attentionPoint: KudosDto) => {
    setAttentionPointToDelete(attentionPoint)
    setDeleteModalVisible(true)
  }

  const handleDeleteClose = () => {
    setDeleteModalVisible(false)
    setAttentionPointToDelete(null)
  }

  const handleViewClick = (attentionPoint: KudosDto) => {
    setAttentionPointToView(attentionPoint)
    setViewModalVisible(true)
  }

  const handleViewModalClose = () => {
    setViewModalVisible(false)
    setAttentionPointToView(null)
  }

  const columns = [
    {
      title: t('attentionPoint.table.employee'),
      key: 'employee',
      width: '20%',
      render: (record: KudosDto) => {
        const employee = record.employee
        if (!employee) return '-'

        return (
          <PerAvatarPlus
            firstName={employee.firstName}
            lastName={employee.lastName}
            nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
            infoText={employee.companyName}
            nameType={record.employeeId === user.employeeId ? 'you' : 'fullName'}
          />
        )
      },
    },
    {
      title: t('attentionPoint.table.description'),
      dataIndex: 'description',
      key: 'description',
      width: '40%',
      ellipsis: true,
      render: (description: string) => (
        <div className="overflow--hidden">
          <PerText fontSize="m" className="ellipsis--line-2">
            {description}
          </PerText>
        </div>
      ),
    },
    {
      title: t('attentionPoint.table.author'),
      key: 'author',
      width: '20%',
      render: (record: KudosDto) => {
        const author = record.author
        if (!author) return '-'

        return (
          <PerAvatarPlus
            firstName={author.firstName}
            lastName={author.lastName}
            nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
            infoText={author.companyName}
            nameType={record.authorId === user.employeeId ? 'you' : 'fullName'}
          />
        )
      },
    },
    {
      title: t('attentionPoint.table.date'),
      dataIndex: 'createdDate',
      key: 'createdDate',
      width: '9%',
      render: (date: string) => <PerText fontSize="xs">{formatDateShort(date)}</PerText>,
    },
    {
      title: t('attentionPoint.table.actions'),
      key: 'actions',
      width: '11%',
      className: 'textAlign-right',
      render: (record: KudosDto) => (
        <PerTableActionButtons
          variant="pastel"
          buttons={[
            {
              icon: 'delete_fill',
              showOnlyWhen: record.deletable,
              color: 'error',
              tooltip: t('attentionPoint.table.delete'),
              onClick: () => handleDeleteClick(record),
            },
            {
              icon: 'view_fill',
              color: 'primary',
              tooltip: t('attentionPoint.table.view'),
              onClick: () => handleViewClick(record),
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <Table
        className={classNames('light small scroll-compensation', {
          viewKudosTableAndShowMoreButton: showMoreButtonVisible,
          viewKudosTable: !showMoreButtonVisible,
        })}
        columns={columns}
        dataSource={attentionPoints.map((attentionPoint) => ({ ...attentionPoint, key: attentionPoint.id }))}
        pagination={false}
        rowKey="id"
        rowClassName={() => 'minHeight-row'}
        scroll={{ y: true }}
        locale={{
          emptyText: (
            <PerEmpty
              heading={t('attentionPoint.table.noAttentionPointsWithFilters')}
              info={t('attentionPoint.table.noAttentionPointsWithFiltersInfo')}
              icon
            />
          ),
        }}
      />

      <DeleteAttentionPointModal
        visible={deleteModalVisible}
        onClose={handleDeleteClose}
        attentionPoint={attentionPointToDelete}
        filters={filters}
      />

      {attentionPointToView && (
        <AttentionPointDetailsModal visible={viewModalVisible} onClose={handleViewModalClose} attentionPoint={attentionPointToView} />
      )}
    </>
  )
}
